import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Scale, RadioTower, LogOut, Users, Bell, MessagesSquare, IdCard, CreditCard, ScrollText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { StatsBar } from "@/components/StatsBar";
import { WhatsAppSimulator } from "@/components/WhatsAppSimulator";
import { RequestsFeed } from "@/components/RequestsFeed";
import { CaseRecordsTable } from "@/components/CaseRecordsTable";
import { CaseRecordModal } from "@/components/CaseRecordModal";
import { ConversationLogs } from "@/components/ConversationLogs";
import { ConversationLayerControls } from "@/components/ConversationLayerControls";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({});
  const [requests, setRequests] = useState([]);
  const [cases, setCases] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/stats"); setStats(data); } catch {}
  }, []);

  const loadRequests = useCallback(async () => {
    try { const { data } = await api.get("/requests", { params: { status: filter } }); setRequests(data); } catch {}
  }, [filter]);

  const loadCases = useCallback(async () => {
    try { const { data } = await api.get("/cases", { params: search ? { search } : {} }); setCases(data); } catch {}
  }, [search]);

  useEffect(() => { loadRequests(); }, [loadRequests]);
  useEffect(() => { const t = setTimeout(loadCases, 250); return () => clearTimeout(t); }, [loadCases]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const refreshAll = useCallback(() => { loadStats(); loadRequests(); loadCases(); }, [loadStats, loadRequests, loadCases]);

  const onNew = () => { setEditing(null); setModalOpen(true); };
  const onEdit = (c) => { setEditing(c); setModalOpen(true); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/cases/${deleteTarget.id}`);
      toast.success("Case record deleted");
      setDeleteTarget(null);
      refreshAll();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="min-h-screen bg-[--w-bg] pb-16 text-[--w-text]">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-[--w-border] bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 ring-1 ring-blue-200">
              <Scale className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold tracking-tight text-[--w-text]">Dicta</h1>
              <p className="font-mono-data text-[11px] text-[--w-text-3]">WhatsApp Case Lookup Automation · Beta</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              data-testid="twilio-status"
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                stats.twilio_live
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700"
              }`}
            >
              <RadioTower className="h-3.5 w-3.5" />
              {stats.twilio_live ? "Twilio Live" : "Simulation Mode"}
            </span>
            {stats.app_mode === "production" && (
              <span
                data-testid="app-mode-badge"
                className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700"
              >
                PRODUCTION
              </span>
            )}
            {stats.app_mode === "demo" && (
              <span
                data-testid="app-mode-badge"
                className="flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700"
              >
                DEMO DATA
              </span>
            )}
            <Link
              to="/members"
              data-testid="members-nav-link"
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-[--w-text-2] transition-colors duration-200 hover:bg-[--w-card-2] hover:text-[--w-text]"
            >
              <IdCard className="h-4 w-4 sm:mr-0.5" />
              <span className="hidden sm:inline">Members</span>
            </Link>
            <Link
              to="/conversations"
              data-testid="conversations-nav-link"
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-[--w-text-2] transition-colors duration-200 hover:bg-[--w-card-2] hover:text-[--w-text]"
            >
              <MessagesSquare className="h-4 w-4 sm:mr-0.5" />
              <span className="hidden sm:inline">Chats</span>
            </Link>
            <Link
              to="/team"
              data-testid="team-nav-link"
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-[--w-text-2] transition-colors duration-200 hover:bg-[--w-card-2] hover:text-[--w-text]"
            >
              <Users className="h-4 w-4 sm:mr-0.5" />
              <span className="hidden sm:inline">Team</span>
            </Link>
            <Link
              to="/alerts"
              data-testid="alerts-nav-link"
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-[--w-text-2] transition-colors duration-200 hover:bg-[--w-card-2] hover:text-[--w-text]"
            >
              <Bell className="h-4 w-4 sm:mr-0.5" />
              <span className="hidden sm:inline">Alerts</span>
            </Link>
            <Link
              to="/payments"
              data-testid="payments-nav-link"
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-[--w-text-2] transition-colors duration-200 hover:bg-[--w-card-2] hover:text-[--w-text]"
            >
              <CreditCard className="h-4 w-4 sm:mr-0.5" />
              <span className="hidden sm:inline">Payments</span>
            </Link>
            <Link
              to="/trace"
              data-testid="trace-nav-link"
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-[--w-text-2] transition-colors duration-200 hover:bg-[--w-card-2] hover:text-[--w-text]"
            >
              <ScrollText className="h-4 w-4 sm:mr-0.5" />
              <span className="hidden sm:inline">Trace</span>
            </Link>
            <span className="hidden font-mono-data text-xs text-[--w-text-2] sm:inline" data-testid="current-user-email">
              {user?.email}
            </span>
            <Button
              data-testid="logout-button"
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-[--w-text-2] hover:bg-[--w-card-2] hover:text-[--w-text]"
            >
              <LogOut className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] space-y-5 px-5 pt-6">
        <StatsBar stats={stats} />

        <ConversationLayerControls />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <RequestsFeed
              requests={requests}
              onSelect={() => {}}
              onRefresh={refreshAll}
              filter={filter}
              setFilter={setFilter}
            />
          </div>
          <div className="lg:col-span-5">
            <WhatsAppSimulator onResolved={refreshAll} />
          </div>
        </div>

        <CaseRecordsTable
          cases={cases}
          search={search}
          setSearch={setSearch}
          onNew={onNew}
          onEdit={onEdit}
          onDelete={setDeleteTarget}
        />

        <ConversationLogs requests={requests} />
      </main>

      <CaseRecordModal open={modalOpen} onOpenChange={setModalOpen} editing={editing} onSaved={refreshAll} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="border-[--w-border] bg-[--w-card] text-[--w-text]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete case record?</AlertDialogTitle>
            <AlertDialogDescription className="text-[--w-text-2]">
              This will permanently remove <span className="font-mono-data text-blue-600">{deleteTarget?.case_number}</span> from the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[--w-border] bg-transparent text-[--w-text-2] hover:bg-[--w-card-2] hover:text-[--w-text]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-case" onClick={confirmDelete} className="bg-red-600 text-white hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
